<template>
    <Dialog :open="open" @update:open="$emit('close')">
        <DialogContent class="sm:max-w-3xl max-h-[90dvh] overflow-y-auto md:overflow-hidden">
            <DialogHeader>
                <DialogTitle>Your order</DialogTitle>
            </DialogHeader>

            <!-- Loading skeleton -->
            <div v-if="loadingPrepare" class="flex flex-col md:flex-row gap-6 md:min-h-[340px]">
                <div class="flex-1 rounded-none border border-new-neutral-200 p-6 space-y-4">
                    <div class="h-5 w-48 rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="h-4 w-full rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="h-4 w-3/4 rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="h-4 w-2/3 rounded bg-new-neutral-200 animate-pulse mt-8"></div>
                </div>
                <div class="w-full md:w-[300px] md:shrink-0 rounded-none bg-new-neutral-100 p-6 space-y-4">
                    <div class="h-5 w-24 rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="h-4 w-full rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="h-3 w-2/3 rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="h-3 w-3/4 rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="flex-1"></div>
                    <div class="h-px w-full bg-new-neutral-200 mt-12"></div>
                    <div class="h-4 w-full rounded bg-new-neutral-200 animate-pulse"></div>
                    <div class="h-10 w-full rounded bg-new-neutral-200 animate-pulse"></div>
                </div>
            </div>

            <!-- Error -->
            <div v-else-if="!preparedSubscription && !loadingPrepare" class="flex h-60 items-center justify-center">
                <p class="text-sm text-muted-foreground">Failed to load subscription details. Please try again.</p>
            </div>

            <!-- Two-panel layout -->
            <div v-else-if="preparedSubscription" class="flex flex-col md:flex-row gap-6 md:min-h-[340px] md:max-h-[calc(90dvh-120px)]">
                <!-- Left panel -->
                <div class="flex-1 md:overflow-y-auto">
                    <!-- Step 1: Plan details -->
                    <div v-if="step === 'summary'" class="rounded-none border border-new-neutral-200 p-6 h-full">
                        <h3 class="text-base font-semibold text-foreground">
                            TOPOL {{ selectedPlan?.name }}
                        </h3>
                        <p class="mt-3 text-sm text-muted-foreground">
                            {{ selectedPlan?.description }}
                            <span v-if="selectedPlan?.users_limit">
                                {{ selectedPlan.users_limit }} users included in plan, additional user for ${{ (selectedPlan.users_price || 0) / 100 }}/month.
                            </span>
                            Paid {{ selectedPlan?.period === 12 ? 'annually' : 'monthly' }}.
                        </p>
                        <p class="mt-6 text-sm text-muted-foreground">
                            Additional users above plan are billed extra on the end of billing period.
                        </p>
                    </div>

                    <!-- Step 2: Payment method selection -->
                    <div v-else-if="step === 'payment'">
                        <PaymentMethodCard
                            :billing-address="billingAddress"
                            :countries="countries"
                            :hide-card-wrapper="true"
                            @select-payment-method="handleSelectPaymentMethod"
                        />
                        <div v-if="subscriptionError" class="mt-4 rounded-none bg-new-red-100 p-3">
                            <p class="text-sm text-new-red-600">{{ subscriptionError }}</p>
                        </div>
                    </div>

                    <!-- Step 3: Success -->
                    <div v-else-if="step === 'success'" class="flex flex-col items-center justify-center h-full gap-4">
                        <div class="flex size-16 items-center justify-center rounded-full bg-new-green-100">
                            <Check class="size-8 text-new-green-600" />
                        </div>
                        <h3 class="text-lg font-semibold text-foreground">Subscription created!</h3>
                        <p class="text-sm text-muted-foreground">Your subscription is now active.</p>
                    </div>
                </div>

                <!-- Right panel: Summary -->
                <div class="w-full md:w-[300px] md:shrink-0 rounded-none bg-new-blue-50 p-6 flex flex-col">
                    <h3 class="text-base font-semibold text-foreground mb-6">Summary</h3>

                    <div class="space-y-1 flex-1">
                        <div class="flex justify-between">
                            <span class="text-sm font-medium text-foreground">{{ selectedPlan?.name }}</span>
                            <span class="text-sm font-medium text-foreground">
                                ${{ ((effectiveSubscription?.price ?? 0) / 100).toFixed(0) }}/{{ selectedPlan?.period === 12 ? 'year' : 'month' }}
                            </span>
                        </div>
                        <p v-if="selectedPlan?.users_limit" class="text-xs text-muted-foreground">
                            {{ selectedPlan.users_limit }} users included in plan.
                        </p>
                        <p class="text-xs text-muted-foreground">
                            {{ selectedPlan?.period === 12 ? 'Annual' : 'Monthly' }} plan renews {{ renewalDate }}
                        </p>

                        <!-- Coupon section -->
                        <div v-if="step !== 'success'" class="mt-4 pt-4 border-t border-new-neutral-300 space-y-2">
                            <div v-if="appliedCoupon" class="flex items-center justify-between gap-2">
                                <div class="flex items-center gap-1.5 text-xs">
                                    <CheckCircle2 class="size-3.5 text-new-green-600" />
                                    <span class="font-medium text-foreground">Coupon: {{ appliedCoupon }}</span>
                                </div>
                                <button
                                    type="button"
                                    class="text-xs text-muted-foreground hover:text-new-red-600"
                                    @click="removeCoupon"
                                >
                                    Remove
                                </button>
                            </div>
                            <div v-else-if="!showCouponInput" class="flex justify-end">
                                <button
                                    type="button"
                                    class="text-xs font-medium text-new-blue-500 hover:text-new-blue-600"
                                    @click="showCouponInput = true"
                                >
                                    Apply coupon
                                </button>
                            </div>
                            <div v-else class="flex gap-2">
                                <Input
                                    v-model="couponCode"
                                    placeholder="Coupon code"
                                    class="h-8 text-xs"
                                    :disabled="loadingCoupon"
                                />
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    :disabled="!couponCode.trim() || loadingCoupon"
                                    @click="applyCoupon"
                                >
                                    {{ loadingCoupon ? '…' : 'Apply' }}
                                </Button>
                            </div>
                            <p v-if="couponError" class="text-xs text-new-red-600">{{ couponError }}</p>
                        </div>
                    </div>

                    <div class="border-t border-new-neutral-300 pt-4 mt-6 space-y-4">
                        <div v-if="effectiveSubscription && effectiveSubscription.subtotal !== effectiveSubscription.total" class="flex justify-between">
                            <span class="text-xs text-muted-foreground">Subtotal</span>
                            <span class="text-xs text-muted-foreground">${{ ((effectiveSubscription.subtotal ?? 0) / 100).toFixed(2) }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-sm font-semibold text-foreground">
                                {{ effectiveSubscription && effectiveSubscription.subtotal !== effectiveSubscription.total ? 'Total (incl. tax)' : 'Total due today' }}
                            </span>
                            <span class="text-sm font-semibold text-foreground">${{ ((effectiveSubscription?.total ?? 0) / 100).toFixed(2) }}</span>
                        </div>

                        <Button
                            v-if="step === 'summary'"
                            class="w-full"
                            size="lg"
                            @click="step = 'payment'"
                        >
                            Continue to checkout
                        </Button>

                        <Button
                            v-else-if="step === 'payment'"
                            class="w-full"
                            size="lg"
                            :disabled="!selectedCard.token || subscribing"
                            @click="handleSubmit"
                        >
                            {{ subscribing ? 'Processing…' : 'Subscribe' }}
                        </Button>

                        <Button
                            v-else-if="step === 'success'"
                            class="w-full"
                            size="lg"
                            @click="$emit('close')"
                        >
                            Done
                        </Button>

                        <p v-if="step !== 'success'" class="text-xs text-muted-foreground">
                            You'll be charged for individual products at the end of their free trial(s). Your subscription will continue until you cancel.
                        </p>
                    </div>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, watch, computed, onMounted, PropType } from 'vue';
import axios from 'axios';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { format, addMonths, addYears } from 'date-fns';
import { Check, CheckCircle2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import PaymentMethodCard from './PaymentMethodCard.vue';
import SubscriptionPlan from '@/types/Subscription/SubscriptionPlan';
import BillingAddress from '@/types/Subscription/BillingAddress';
import {
    ICreditCardPaymentMethod,
    IPaymentMethod,
    IStripeCreditCardPaymentMethod,
} from '@/types/Subscription/IPaymentMethod';
import useFeatures from '@/composables/useFeatures';
import { useBraintreeClient } from '@/composables/useBraintree';
import { useStripeClient } from '@/composables/useStripe';
import { useGtm } from '@/composables/useGtm';

interface ISelectedCard {
    id: string;
    token: string;
    type: 'Card' | 'PayPal' | '';
}

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    planUuid: {
        type: String,
        required: true,
    },
    subscriptionPlans: {
        type: Array as PropType<SubscriptionPlan[]>,
        required: true,
    },
    billingAddress: {
        type: Object as PropType<BillingAddress>,
        required: true,
    },
    countries: {
        type: Array as PropType<Array<{ value: string; title: string }>>,
        required: true,
    },
    seats: {
        type: Number,
        default: 0,
    },
    personal: {
        type: Boolean,
        default: false,
    },
    autoCoupon: {
        type: String,
        default: null,
    },
});

const emit = defineEmits<{
    close: [];
    subscribed: [];
}>();

const step = ref<'summary' | 'payment' | 'success'>('summary');
const loadingPrepare = ref(false);
const preparedSubscription = ref<any>(null);
const updatedSubscription = ref<any>(null);
const selectedCard = ref<ISelectedCard>({ id: '', token: '', type: '' });
const subscribing = ref(false);
const subscriptionError = ref('');

const couponCode = ref('');
const appliedCoupon = ref('');
const showCouponInput = ref(false);
const couponError = ref('');
const loadingCoupon = ref(false);

const { teamFeatureDisabled } = useFeatures();
const { pushEvent } = useGtm();

const isStripeEnabled = computed(() => !teamFeatureDisabled('stripe'));

const selectedPlan = computed(() => {
    return props.subscriptionPlans.find((p) => p.uuid === props.planUuid);
});

const effectiveSubscription = computed(() => updatedSubscription.value ?? preparedSubscription.value);

const planType = computed<'pro' | 'plugin'>(() => {
    return (selectedPlan.value?.type === 'pro' ? 'pro' : 'plugin');
});

const { createClient: createBraintreeClient, verifyCardByToken } = useBraintreeClient(planType.value);
const { confirmStripeCardPayment } = useStripeClient(planType.value);

const renewalDate = computed(() => {
    if (!selectedPlan.value) return '';
    const now = new Date();
    const next = selectedPlan.value.period === 12 ? addYears(now, 1) : addMonths(now, 1);
    return format(next, 'MMM d, yyyy');
});

const fetchPreparedSubscription = async (couponCodeValue?: string) => {
    loadingPrepare.value = true;
    try {
        const params: Record<string, unknown> = {
            subscription_plan_uuid: props.planUuid,
            additional_users: props.seats,
        };
        if (couponCodeValue) {
            params.coupon_code = couponCodeValue;
        }
        const res = await axios.get(route('subscriptions.prepare', params));
        return res.data.data;
    } catch (e) {
        console.error('Failed to prepare subscription:', e);
        toast.error('Failed to load subscription details');
        return null;
    } finally {
        loadingPrepare.value = false;
    }
};

const initialPrepare = async () => {
    step.value = 'summary';
    preparedSubscription.value = null;
    updatedSubscription.value = null;
    selectedCard.value = { id: '', token: '', type: '' };
    subscriptionError.value = '';
    appliedCoupon.value = '';
    couponCode.value = '';
    couponError.value = '';
    showCouponInput.value = false;

    preparedSubscription.value = await fetchPreparedSubscription();

    if (props.autoCoupon && props.autoCoupon.trim()) {
        couponCode.value = props.autoCoupon.trim();
        await applyCoupon();
    }
};

onMounted(() => {
    if (props.open) {
        initialPrepare();
    }
});

watch(() => props.open, (val) => {
    if (val) {
        initialPrepare();
    }
});

const handleSelectPaymentMethod = (paymentMethod: IPaymentMethod) => {
    selectedCard.value = {
        id: (paymentMethod as IStripeCreditCardPaymentMethod)?.id ?? '',
        token: paymentMethod.token,
        type:
            (paymentMethod as ICreditCardPaymentMethod).last4 !== undefined
                ? 'Card'
                : 'PayPal',
    };
};

const handleSubmit = async () => {
    if (!selectedCard.value.token) {
        return;
    }

    subscribing.value = true;
    subscriptionError.value = '';

    try {
        if (isStripeEnabled.value) {
            await buySubscriptionWithStripe();
        } else {
            await buySubscriptionWithBraintree();
        }
    } catch {
        // already handled in inner promises (toast + subscriptionError)
    } finally {
        subscribing.value = false;
    }
};

const buySubscriptionWithStripe = () => {
    return new Promise((resolve, reject) => {
        router.post(
            route('stripe.subscriptions.store'),
            {
                subscription_plan_uuid: props.planUuid,
                payment_method_id: selectedCard.value.id,
                coupon_code: appliedCoupon.value || null,
                personal: props.personal,
            },
            {
                preserveScroll: true,
                onSuccess: async (response: any) => {
                    if (
                        response.props?.flash?.data?.paymentIntent?.status ===
                        'requires_confirmation'
                    ) {
                        const result = await confirmStripeCardPayment(
                            response.props.flash.data.paymentIntent.client_secret
                        );

                        if (!result) {
                            const message = 'Verifying card with 3D Secure failed';
                            subscriptionError.value = message;
                            toast.error(message);
                            reject(response);
                            return;
                        }

                        router.post(
                            route(
                                'stripe.subscriptions.complete',
                                response.props.flash.data.subscription.id
                            )
                        );
                    }

                    pushEvent('topol_purchase_first', {
                        type: planType.value,
                        amountPaid: (effectiveSubscription.value?.total ?? 0) / 100,
                        planType: selectedPlan.value?.period === 1 ? 'monthly' : 'yearly',
                        currency: 'USD',
                    });

                    toast.success('Subscription created');
                    step.value = 'success';
                    emit('subscribed');
                    // Force fresh page props so the canceled-state UI flips back
                    // to active immediately on the underlying billing page.
                    router.reload({
                        only: ['pluginSubscription', 'shouldAllowTrial'],
                        preserveScroll: true,
                        preserveState: true,
                    });
                    resolve(response);
                },
                onError: (errors: Record<string, string>) => {
                    const message = Object.values(errors)[0] ?? 'Subscription failed. Please try again.';
                    subscriptionError.value = message;
                    toast.error(message);
                    reject(errors);
                },
            }
        );
    });
};

const buySubscriptionWithBraintree = async () => {
    await createBraintreeClient({ enableHostedFields: false });

    let verifyCardResponse: { nonce: string } | undefined;

    if (selectedCard.value.type === 'Card') {
        verifyCardResponse = await verifyCardByToken({
            token: selectedCard.value.token,
            amount: effectiveSubscription.value?.total ?? 0,
        });
    }

    return new Promise((resolve, reject) => {
        router.post(
            route('subscriptions.store'),
            {
                subscription_plan_uuid: props.planUuid,
                type: selectedCard.value.type,
                nonce: verifyCardResponse ? verifyCardResponse.nonce : '',
                token: selectedCard.value.token,
                additional_users: props.seats,
                personal: props.personal,
            },
            {
                preserveScroll: true,
                onSuccess: (response: any) => {
                    toast.success('Subscription created');
                    step.value = 'success';
                    emit('subscribed');
                    router.reload({
                        only: ['pluginSubscription', 'shouldAllowTrial'],
                        preserveScroll: true,
                        preserveState: true,
                    });
                    resolve(response);
                },
                onError: (errors: Record<string, string>) => {
                    const message = Object.values(errors)[0] ?? 'Subscription failed. Please try again.';
                    subscriptionError.value = message;
                    toast.error(message);
                    reject(errors);
                },
            }
        );
    });
};

const applyCoupon = async () => {
    if (!couponCode.value.trim()) return;

    loadingCoupon.value = true;
    couponError.value = '';

    try {
        const data = await fetchPreparedSubscription(couponCode.value.trim());
        if (data) {
            updatedSubscription.value = data;
            appliedCoupon.value = couponCode.value.trim();
            showCouponInput.value = false;
            couponCode.value = '';
            toast.success(`Coupon "${appliedCoupon.value}" applied`);
        }
    } catch (error: any) {
        if (error?.response?.status === 422) {
            couponError.value = 'Invalid coupon code';
        } else if (error?.response?.data?.message) {
            couponError.value = error.response.data.message;
        } else {
            couponError.value = 'Failed to apply coupon';
        }
        toast.error(couponError.value);
    } finally {
        loadingCoupon.value = false;
    }
};

const removeCoupon = () => {
    appliedCoupon.value = '';
    updatedSubscription.value = null;
    couponCode.value = '';
    couponError.value = '';
    showCouponInput.value = false;
    toast.info('Coupon removed');
};
</script>
